import { create } from "zustand";
import { persist } from "zustand/middleware";

import type {
  LoginRequest,
  RegisterRequest,
  User,
  UserRole,
} from "@/types/auth";
import { i18nToast } from "@/utils/i18nToast";
import {
  SecondFactorRequiredError,
  type FactorChallenge,
} from "@/services/secondFactor";
import { log } from "@/lib/log";
import { isStaffAdmin } from "@/utils/staffRoles";

/** A password plus, on the second round-trip, the factor it turned out to need. */
export interface LoginCredentials extends Omit<LoginRequest, "password"> {
  /** Absent when a passkey is the whole login rather than the second step. */
  password?: string;
  factor?: string;
  mfaToken?: string;
  /**
   * A single-use proof that a passkey assertion was verified, from
   * passkeyService.signIn(). The backend passes it to Keycloak, which redeems
   * it inside the direct grant flow — nothing on this side is trusted with the
   * decision.
   */
  passkeyProof?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  /** Resolves once the device is cleared; navigate away only after that. */
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  // Role management functions
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAdmin: () => boolean;
  isHealthcareProvider: () => boolean;
  isHealthcareProviderAdmin: () => boolean;
  isTourOperator: () => boolean;
  isTourOperatorAdmin: () => boolean;
  isAffiliatePartner: () => boolean;
  isAffiliatePartnerAdmin: () => boolean;
  canAccessAdminPanel: () => boolean;
  canManageTours: () => boolean;
  canManageHealthcareServices: () => boolean;
  canManageAffiliatePartnership: () => boolean;
  canAccessTourOperatorPanel: () => boolean;
  canAccessHealthcareProviderPanel: () => boolean;
  canAccessAffiliatePartnerPanel: () => boolean;
}

type AuthStore = AuthState & AuthActions;

/**
 * Fetch the signed-in user's picture and put it in the store.
 *
 * Used both after a login and when a persisted session is restored: the URL is
 * deliberately not persisted (see `partialize`), so every start needs it again.
 * A failure is ignored - an app without a face in the corner still works.
 */
async function fetchAvatarInto(
  set: (partial: Partial<AuthStore>) => void,
  token: string,
  keycloakId: string,
): Promise<void> {
  const apiUrl = import.meta.env.VITE_API_URL || "/api";
  try {
    const response = await fetch(`${apiUrl}/users/avatar/${keycloakId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;
    const body = (await response.json()) as {
      data?: { avatarUrl?: string; thumbnailUrl?: string };
    };
    const url = body?.data?.thumbnailUrl ?? body?.data?.avatarUrl;
    if (!url) return;

    set({
      user: {
        ...(useAuthStore.getState().user as NonNullable<AuthState["user"]>),
        avatarUrl: url,
      },
    });
    window.dispatchEvent(
      new CustomEvent("avatarUpdated", { detail: { avatarUrl: url } }),
    );
  } catch {
    // Non-critical.
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      refreshToken: null,
      isLoading: false,
      isAuthenticated: false,

      // Actions
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true });

          // Login via Keycloak backend API
          const apiUrl = import.meta.env.VITE_API_URL || "/api";
          const response = await fetch(`${apiUrl}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(credentials),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => null);

            // Not a failure: the password was right and a factor is now owed.
            // Raised as its own type so the screen does not tell the user their
            // credentials were wrong when they were not.
            if (errorData?.error?.code === "FACTOR_REQUIRED") {
              set({ isLoading: false });
              throw new SecondFactorRequiredError(
                errorData.error.message || "A second factor is required",
                (errorData.error.details as FactorChallenge | null) ?? null,
              );
            }

            const message =
              response.status === 429
                ? "Too many requests. Please try again in a few minutes."
                : errorData?.error?.message ||
                  errorData?.message ||
                  "Login failed";
            throw new Error(message);
          }

          const data = await response.json();

          // DEBUG: Log the complete user object from login response
          log.debug("🔍 Login response data:", data);
          log.debug("🔍 Login user object:", data.data.user);
          log.debug("🔍 Login user.dateOfBirth:", data.data.user?.dateOfBirth);
          log.debug("🔍 Login user.avatarUrl:", data.data.user?.avatarUrl);

          // Ensure all auth state is set together
          set({
            user: data.data.user,
            token: data.data.token,
            refreshToken: data.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });

          // If avatarUrl is missing, fetch it separately via the avatar API
          if (!data.data.user?.avatarUrl && data.data.user?.keycloakId) {
            void fetchAvatarInto(
              set,
              data.data.token,
              data.data.user.keycloakId,
            );
          }

          // Log successful login for debugging
          log.debug("✅ AuthStore: Keycloak login successful", {
            user: data.data.user.email,
            hasToken: !!data.data.token,
            hasRefreshToken: !!data.data.refreshToken,
            isAuthenticated: true,
          });
        } catch (error: any) {
          // A factor being owed is part of a successful sign-in, not a failed
          // one, so it is re-thrown without the toast or the state reset.
          if (error instanceof SecondFactorRequiredError) {
            throw error;
          }

          console.error("❌ AuthStore: Login failed", error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          i18nToast.loginFailed();
          throw error;
        }
      },

      register: async (userData: RegisterRequest) => {
        try {
          set({ isLoading: true });

          // Register via Keycloak backend API
          const apiUrl = import.meta.env.VITE_API_URL || "/api";
          const response = await fetch(`${apiUrl}/auth/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(userData),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
              errorData?.error?.message ||
                errorData?.message ||
                "Registration failed",
            );
          }

          const data = await response.json();

          // Auto-login after successful registration
          if (data.success) {
            await get().login({
              email: userData.email,
              password: userData.password,
            });
            const currentUser = get().user;
            i18nToast.registrationSuccess(currentUser?.firstName || "User");
          }
        } catch (error: any) {
          set({ isLoading: false });
          i18nToast.registrationFailed();
          throw error;
        }
      },

      logout: async () => {
        // Ends the Keycloak session and clears the health data this device
        // holds, not just these fields - see utils/secureLogout.ts. Loaded on
        // demand because it imports the other stores, one of which reaches
        // this module again through the API client.
        const { secureLogout } = await import("@/utils/secureLogout");
        await secureLogout();
      },

      initializeAuth: async () => {
        const token = get().token;
        const user = get().user;

        if (!token || !user) {
          set({
            isLoading: false,
            isAuthenticated: false,
            user: null,
            token: null,
          });
          return;
        }

        try {
          set({ isLoading: true });

          // Token and user exist from persisted storage
          // Set authenticated state
          set({
            isAuthenticated: true,
            isLoading: false,
          });

          log.debug("✅ Auth initialized from storage", {
            email: user.email,
          });

          // The picture is not persisted, so a restored session starts without
          // one and asks for it here - the same call login makes.
          if (!user.avatarUrl && user.keycloakId) {
            void fetchAvatarInto(set, token, user.keycloakId);
          }
        } catch (error: any) {
          console.error("Auth initialization failed:", error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          log.debug("AuthStore: Updating user data:", userData);
          log.debug("AuthStore: Current user before update:", currentUser);

          const updatedUser = { ...currentUser, ...userData };
          log.debug("AuthStore: Updated user:", updatedUser);

          set({
            user: updatedUser,
          });
        } else {
          console.warn("AuthStore: Cannot update user - no current user found");
        }
      },

      // Role management functions
      hasRole: (role: UserRole) => {
        const { user } = get();
        return user?.role === role;
      },

      hasAnyRole: (roles: UserRole[]) => {
        const { user } = get();
        return user ? roles.includes(user.role) : false;
      },

      isAdmin: () => {
        // Read from the token, not from the profile. `user.role` is a value
        // this application stores; a realm role is one only Keycloak can
        // grant, and it is what the backend checks (REQ-015).
        const { token } = get();
        return isStaffAdmin(token);
      },

      isHealthcareProvider: () => {
        const { user } = get();
        return user?.role === "HEALTHCARE_PROVIDER";
      },

      isTourOperator: () => {
        const { user } = get();
        return user?.role === "TOUR_OPERATOR";
      },

      isTourOperatorAdmin: () => {
        const { user } = get();
        return user?.role === "TOUR_OPERATOR_ADMIN";
      },

      isAffiliatePartner: () => {
        const { user } = get();
        return user?.role === "AFFILIATE_PARTNER";
      },

      isAffiliatePartnerAdmin: () => {
        const { user } = get();
        return user?.role === "AFFILIATE_PARTNER_ADMIN";
      },

      isHealthcareProviderAdmin: () => {
        const { user } = get();
        return user?.role === "HEALTHCARE_PROVIDER_ADMIN";
      },

      canAccessAdminPanel: () => {
        const { user } = get();
        return user?.role === "ADMIN" || user?.role === "HEALTHCARE_PROVIDER";
      },

      canManageTours: () => {
        const { user } = get();
        return user?.role === "ADMIN" || user?.role === "TOUR_OPERATOR_ADMIN";
      },

      canManageHealthcareServices: () => {
        const { user } = get();
        return (
          user?.role === "ADMIN" || user?.role === "HEALTHCARE_PROVIDER_ADMIN"
        );
      },

      canManageAffiliatePartnership: () => {
        const { user } = get();
        return (
          user?.role === "ADMIN" || user?.role === "AFFILIATE_PARTNER_ADMIN"
        );
      },

      canAccessTourOperatorPanel: () => {
        const { user } = get();
        return user?.role === "TOUR_OPERATOR_ADMIN" || user?.role === "ADMIN";
      },

      canAccessHealthcareProviderPanel: () => {
        const { user } = get();
        return (
          user?.role === "HEALTHCARE_PROVIDER_ADMIN" || user?.role === "ADMIN"
        );
      },

      canAccessAffiliatePartnerPanel: () => {
        const { user } = get();
        return (
          user?.role === "AFFILIATE_PARTNER_ADMIN" || user?.role === "ADMIN"
        );
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        // The avatar URL is not part of the profile, it is a fetched picture -
        // once a presigned link that expired in fifteen minutes, now inline
        // data worth a hundred kilobytes. Kept, it survived every deploy and
        // every reload: a broken image from yesterday's signature, which is
        // exactly what the settings page kept reporting after the URL itself
        // had been fixed on the server. It is fetched again at startup.
        user: state.user ? { ...state.user, avatarUrl: undefined } : null,
        isAuthenticated: state.isAuthenticated,
      }),
      /**
       * Drop the picture on the way in as well as on the way out.
       *
       * `partialize` only governs what is written, so every browser that had
       * already stored a URL kept loading it back and rendering it - the
       * settings page went on reporting the same expired link from a host that
       * does not resolve, on a build that no longer produces one. What is
       * already in a hundred browsers has to be discarded where it is read.
       */
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<AuthStore>;
        return {
          ...current,
          ...saved,
          user: saved.user ? { ...saved.user, avatarUrl: undefined } : null,
        };
      },
    },
  ),
);

// HMR Support - Preserve auth state during hot reloads
if (import.meta.hot) {
  import.meta.hot.accept((newModule: unknown) => {
    if (newModule) {
      log.debug("🔄 AuthStore HMR updated");
    }
  });
}
